import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import Loading from "components/Loading";
import { composeDeck } from "services/trainingDeckService";

const TrainingDeckViewer = ({ deckId, userHierarchyPath, onComplete }) => {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const composed = await composeDeck(deckId, userHierarchyPath);
        if (active) {
          setSlides(composed);
          setIndex(0);
        }
      } catch (err) {
        console.error("[TrainingDeckViewer] composeDeck failed", err);
        if (active) setSlides([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [deckId, userHierarchyPath?.institutionId, userHierarchyPath?.departmentId, userHierarchyPath?.regionId, userHierarchyPath?.subInstitutionId, userHierarchyPath?.subDepartmentId]);

  const slide = useMemo(() => slides[index] || null, [slides, index]);
  const isLastSlide = slides.length > 0 && index >= slides.length - 1;

  const handleComplete = async () => {
    if (!onComplete) return;
    setCompleting(true);
    try {
      await onComplete();
    } finally {
      setCompleting(false);
    }
  };

  if (loading) return <Loading page />;

  if (!slide) {
    return (
      <Typography color="text.secondary" variant="body2">
        No training deck content is available yet.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="overline" color="text.secondary">
            Slide {index + 1} of {slides.length}
          </Typography>
          {slide.flaggedForUpdate ? (
            <Chip size="small" color="warning" label="Flagged for update" />
          ) : null}
        </Stack>
        <Typography variant="h5">{slide.title}</Typography>
        <Typography variant="body1">{slide.bodyText}</Typography>
        {slide.screenshotDescription ? (
          <Typography variant="body2" color="text.secondary">
            Visual: {slide.screenshotDescription}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <IconButton
            disabled={index <= 0}
            onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
            aria-label="Previous slide"
          >
            <ArrowBackRounded />
          </IconButton>
          {isLastSlide && onComplete ? (
            <LoadingButton
              variant="contained"
              loading={completing}
              onClick={handleComplete}
              data-testid="training-deck-complete-btn"
            >
              Complete orientation
            </LoadingButton>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <IconButton
            disabled={index >= slides.length - 1}
            onClick={() => setIndex((prev) => Math.min(slides.length - 1, prev + 1))}
            aria-label="Next slide"
          >
            <ArrowForwardRounded />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

TrainingDeckViewer.propTypes = {
  deckId: PropTypes.string.isRequired,
  userHierarchyPath: PropTypes.object,
  onComplete: PropTypes.func,
};

export default TrainingDeckViewer;
